import { useState, useEffect } from 'react';
import { 
  addAdminRole,
  removeAdminRole,
  getAllAdmins
} from '../../services/adminService';
import { auth } from '../../config/firebase';

export default function AdminManagementLogic() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Load list of admins when component mounts
  useEffect(() => {
    const loadAdmins = async () => {
      try {
        setLoading(true);
        setError('');
        
        const adminsList = await getAllAdmins();
        setAdmins(adminsList);
      } catch (error) {
        console.error('Error loading admins:', error);
        setError('שגיאה בטעינת רשימת המנהלים');
      } finally {
        setLoading(false);
      }
    };
    
    loadAdmins();
  }, []);
  
  // Handle input change for new admin email
  const handleInputChange = (e) => {
    setNewAdminEmail(e.target.value);
  };
  
  // Add new admin
  const handleAddAdmin = async () => {
    try {
      // Validate email
      if (!newAdminEmail || !newAdminEmail.includes('@')) {
        setError('יש להזין כתובת אימייל תקינה');
        return;
      }
      
      setLoading(true);
      setError('');
      setSuccess(false);
      
      // Call Cloud Function to add admin role
      const result = await addAdminRole(newAdminEmail);
      
      if (result.success) {
        // Reload admins list
        const updatedAdmins = await getAllAdmins();
        setAdmins(updatedAdmins);
        
        // Show success message
        setSuccessMessage(`הרשאות מנהל נוספו למשתמש ${newAdminEmail} בהצלחה`);
        setSuccess(true);
        setNewAdminEmail('');
        
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.error || 'שגיאה בהוספת הרשאות מנהל');
      }
    } catch (error) {
      console.error('Error adding admin:', error);
      
      // Handle specific Firebase error codes
      if (error.code === 'functions/invalid-argument') {
        setError('יש להזין כתובת אימייל תקינה');
      } else if (error.code === 'functions/not-found') {
        setError('המשתמש לא נמצא במערכת. ודא שהמשתמש נרשם קודם.');
      } else {
        setError(`שגיאה בהוספת הרשאות מנהל: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Remove admin
  const handleRemoveAdmin = async (uid) => {
    try {
      // Don't allow removing your own admin rights
      if (uid === auth.currentUser?.uid) {
        setError('לא ניתן להסיר את ההרשאות של עצמך');
        return;
      }
      
      setLoading(true);
      setError('');
      setSuccess(false);
      
      // Find the admin in the list
      const adminToRemove = admins.find(admin => admin.uid === uid);
      if (!adminToRemove) {
        setError('המנהל לא נמצא');
        setLoading(false);
        return;
      }
      
      // Call Cloud Function to remove admin role
      const result = await removeAdminRole(uid);
      
      if (result.success) {
        // Update local list
        setAdmins(admins.filter(admin => admin.uid !== uid));
        
        // Show success message
        setSuccessMessage(`הרשאות מנהל הוסרו מהמשתמש ${adminToRemove.email} בהצלחה`);
        setSuccess(true);
        
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.error || 'שגיאה בהסרת הרשאות מנהל');
      }
    } catch (error) {
      console.error('Error removing admin:', error);
      setError(`שגיאה בהסרת הרשאות מנהל: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return {
    admins,
    loading,
    error,
    newAdminEmail,
    handleInputChange,
    handleAddAdmin,
    handleRemoveAdmin,
    success,
    successMessage
  };
} 